import React, { useMemo } from 'react';
import { getStatusChipStyle } from './StatusChip';
import { TOKEN_STATUS_LABELS } from '../stores/data/types/feTokenState';
import { StateChangeHistoryDto } from '../api/fe-management-system-api/types';

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Seoul',
});

const formatTimestamp = (value: Date | string): string => {
  const date = typeof value === 'string' ? new Date(value) : value;
  return DATE_TIME_FORMATTER.format(date);
};

interface TokenStatusHistoryProps {
  history?: StateChangeHistoryDto[];
}

const TokenStatusHistory: React.FC<TokenStatusHistoryProps> = React.memo(({ history = [] }) => {
  const sortedHistory = useMemo(() => {
    const normalized = history.map((entry) => ({
      status: entry.status,
      changedAt: new Date(entry.changedAt),
    }));
    return normalized.sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
  }, [history]);

  if (!sortedHistory.length) {
    return (
      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-4">
        <div className="text-sm font-semibold text-white mb-3">Status change history</div>
        <p className="text-xs text-gray-500">No status change history has been saved yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-800 bg-gray-900 p-4">
      <div className="text-sm font-semibold text-white mb-3">Status change history</div>
      <div className="flex flex-col gap-3">
        {sortedHistory.map((entry, index) => (
          <div key={`${entry.status}-${entry.changedAt.toISOString()}-${index}`} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full border ${getStatusChipStyle(entry.status)}`}
              >
                {TOKEN_STATUS_LABELS[entry.status] || entry.status}
              </span>
              <span className="text-xs text-gray-400">
                {formatTimestamp(entry.changedAt)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

TokenStatusHistory.displayName = 'TokenStatusHistory';

export default TokenStatusHistory;
